/**
 * Turning an API error into something worth showing a user.
 *
 * The API returns validation failures as
 *   { message: "The vat must be a string.", errors: { vat: ["..."] } }
 * and the toasts were showing `message` verbatim. That is a developer-facing
 * sentence about a field name and a PHP type — exactly the string that appeared
 * on the VAT settings screen. When per-field errors are present the first one is
 * more specific and more useful, and either way there must be a human fallback
 * rather than `undefined`.
 */

type Fallback = string;

/**
 * First per-field validation message if the API sent one, else the top-level
 * message, else the supplied fallback.
 */
export const apiErrorMessage = (err: any, fallback: Fallback): string => {
  const data = err?.response?.data;

  const fieldErrors = data?.errors;
  if (fieldErrors && typeof fieldErrors === "object") {
    for (const value of Object.values(fieldErrors)) {
      if (Array.isArray(value) && typeof value[0] === "string" && value[0].trim()) {
        return value[0];
      }
      if (typeof value === "string" && value.trim()) {
        return value;
      }
    }
  }

  if (typeof data?.message === "string" && data.message.trim()) {
    return data.message;
  }

  return fallback;
};

/**
 * The same, but routed through i18next so a screen can override the API's
 * wording, and keyed on something that does not move.
 *
 * The API used to send English sentences and callers keyed their overrides on
 * them -- `t(`errorLogin.${data.message}`)`. That worked only for as long as
 * the sentence WAS English. It is now localised per request, so every one of
 * those lookups misses and the caller silently drops to its hardcoded default:
 * in user.ts those defaults were French ("Erreur identifiant"), so a German
 * user failing to log in was shown French.
 *
 * `error_key` is the stable identifier the API sends alongside the prose for
 * exactly this purpose. Resolution order:
 *
 *   1. `<namespace>.<error_key>`  - a deliberate local override
 *   2. `<namespace>.<message>`    - legacy sentence-keyed entries, still honoured
 *   3. the API's own `message`    - already in the user's language
 *   4. `fallback`                 - only when the API sent nothing usable
 *
 * Steps 1 and 2 are lookups WITH a default, so a missing entry can never
 * render a raw key.
 */
export const localizedApiError = (
  t: (key: string, fallback: string) => string,
  err: any,
  namespace: string,
  fallback: Fallback
): string => {
  const data = err?.response?.data;
  const best = apiErrorMessage(err, fallback);

  if (typeof data?.error_key === "string" && data.error_key.trim()) {
    return t(`${namespace}.${data.error_key}`, best);
  }

  if (typeof data?.message === "string" && data.message.trim()) {
    return t(`${namespace}.${data.message}`, best);
  }

  return best;
};
